import { ReactNode, useRef, useState } from 'react';
import { PatternFormat, PatternFormatProps } from 'react-number-format';

import { Box } from '@opendoor/bricks/core';
import Field from '@opendoor/cinderblocks/growth/novo/inputs/Field';
import { InputContainer, InputElement } from '@opendoor/cinderblocks/growth/novo/inputs/Input';
import { IAccountFormProps } from '@opendoor/cinderblocks/platform/auth/AccountForm';
import isMobilePhone from 'validator/lib/isMobilePhone';

const PatternInput: React.FC<
  PatternFormatProps & {
    id: string;
    suffix?: ReactNode;
    prefix?: ReactNode;
    error?: boolean;
    disabled?: boolean;
  }
> = ({ suffix, prefix, error, disabled, ...props }) => {
  // setup a ref so that if the user clicks on the padding of InputContainer we
  // can focus them onto our Input
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <InputContainer
      suffix={suffix}
      prefix={prefix}
      error={error}
      disabled={disabled}
      onClick={(event) => event.target !== inputRef.current && inputRef.current?.focus()}
    >
      <PatternFormat
        disabled={disabled}
        {...props}
        customInput={InputElement}
        getInputRef={inputRef}
      />
    </InputContainer>
  );
};

const AccountPhoneInput: IAccountFormProps['PhoneInput'] = ({ ...props }) => {
  const [hasError, setHasError] = useState(false);

  return (
    <Box textAlign="left" mt={8}>
      <Field<PatternFormatProps & { id: string; error?: boolean }>
        errorText="Hm, that doesn't look like a phone number"
        label={props.label}
        InputComponent={PatternInput}
        input={{
          id: props.id,
          format: '(###) ###-####',
          error: hasError,
          onValueChange: (values) => {
            const value = values.formattedValue;
            const isValid = !value || isMobilePhone(value, 'en-US');

            // Clear out any errors if we get a valid email
            if (value && isValid) {
              setHasError(false);
            }
          },
          onChange: (event) => {
            const isValid = !event.target.value || isMobilePhone(event.target.value, 'en-US');
            props.onChange(event, event.target.value, isValid);
          },
          onBlur: (event: React.FocusEvent<HTMLInputElement>) => {
            const value = event.target.value;

            if (!value || isMobilePhone(value, 'en-US')) {
              setHasError(false);
            } else {
              setHasError(true);
            }
          },
          defaultValue: props.value ?? '',
          name: props.name,
          placeholder: '(###) ###-####',
        }}
      />
    </Box>
  );
};

export default AccountPhoneInput;
